import type {
  HandoffContext,
  HandoffContextBlock,
  HandoffContextStatus,
} from 'v2/types/api/handoff-context'

// Only HS writes the policy this setup configures; hide while profile/rollout is unavailable.
export const canConfigureHandoffContext = (enabled: boolean | undefined, flowType?: string) =>
  enabled === true && (flowType === 'incoming_call' || flowType === 'omni')

export const UPLOAD_PATH = 'v1/handoff-context'
export const SIP_HEADER = 'X-PH-HandoffID'
export const NOT_RECEIVED_GRACE_MS = 45_000

/** "collected_fields" -> "Collected fields". */
export const fieldLabel = (field: string): string =>
  field.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** The one shared regional upload URL: same for every Flow of the organization. */
export const uploadUrl = (resolvedApiUrl: string): string =>
  `${resolvedApiUrl.replace(/\/?$/, '/')}${UPLOAD_PATH}`

export const integrationInstructions = (url: string): string =>
  [
    'Plivo CX external AI handoff',
    '1. Generate a fresh uuid4 as 32 lowercase hex chars for this transfer attempt.',
    `2. POST JSON to ${url} with header "Authorization: Bearer <source credential>":`,
    '   {"schema_version":"1.0","handoff_id":"<same id>","revision":1,"completeness":"partial",',
    '    "original_caller":"+15555550100","summary":"...","transfer_reason":"...","collected_fields":{"order_id":"..."},',
    '    "transcript":[{"speaker":"customer","text":"...","offset_ms":1200}]}',
    '   202 stored, 200 duplicate, 409 revision conflict/stale, 410 expired. Retry the same id and revision on uncertain delivery.',
    '3. Transfer the call to the Plivo phone number linked to this Flow: SIP REFER, or a new INVITE to that number.',
    `   Add the SIP header ${SIP_HEADER}: <same id>. Verify the header reaches this Flow on the actual SIP route.`,
    'For PSTN or a route that strips SIP headers: place a Collect response node before Assign to human, enable Receive AI transfer reference, and connect both outputs to Assign to human.',
    '   Send the same UUID as 39 decimal digits on the receiving leg: BigInt("0x" + handoff_id).toString().padStart(39, "0"). Send digits automatically before bridging the customer; do not ask the customer to enter them.',
    '   This uses GetDigits (39 digits), not GetInput (32-digit limit). Configure a short input timeout; missing or invalid digits continue on the configured voice path without context.',
    'SIP REFER works with a reference carried to the receiving leg. If the provider strips it and cannot send post-dial digits, use a bridged transfer with the DTMF fallback; caller number alone is never a match key.',
    'Send the initial caller number and transfer reason before transferring for the best ringing-time display; never delay voice to await context.',
    'original_caller is the original customer number in E.164 format. It is display metadata, not caller-ID verification or a callback target. Omit it for withheld callers.',
    'Every revision is a full snapshot: repeat original_caller and transfer_reason to retain them. Omitted fields clear.',
    'Context may arrive before or after the call; it never waits for the call and never creates one.',
  ].join('\n')

/** Poll only while a fresh pending policy could still receive context; afterwards rely on events. */
export const pollWhilePending = (
  data: HandoffContext | undefined,
  now: number = Date.now(),
): boolean =>
  !!data &&
  data.status === 'pending' &&
  !!data.pending_since &&
  now - Date.parse(data.pending_since) <= NOT_RECEIVED_GRACE_MS

export type PanelState =
  | 'hidden'
  | 'pending'
  | 'not_received'
  | 'available'
  | 'unavailable'
  | 'error'

/** What the agent panel shows for a read result; the grace timer is a UI default, never a call wait. */
export const panelState = (
  data: HandoffContext | undefined,
  isError: boolean,
  now: number = Date.now(),
  httpStatus?: number,
): PanelState => {
  if (isError) {
    // Authorization/deletion wins over cached data. Only transient failures retain a prior view.
    if ([403, 404].includes(httpStatus ?? 0)) return 'hidden'
    if (!data || (httpStatus !== undefined && httpStatus < 500)) {
      return httpStatus === 503 ? 'hidden' : 'error'
    }
  }
  if (!data) return 'hidden'
  const map: Record<HandoffContextStatus, PanelState> = {
    not_configured: 'hidden',
    available: 'available',
    unavailable: 'unavailable',
    pending: pollWhilePending(data, now) ? 'pending' : 'not_received',
  }
  return map[data.status]
}

/** The wire has no turn IDs. Content + duplicate occurrence stays stable when other turns move. */
export const transcriptRows = (turns: NonNullable<HandoffContextBlock['transcript']>) => {
  const occurrences = new Map<string, number>()
  return turns.map((turn) => {
    const identity = JSON.stringify([turn.speaker, turn.text, turn.offset_ms ?? null])
    const occurrence = occurrences.get(identity) || 0
    occurrences.set(identity, occurrence + 1)
    return { turn, key: `${identity}:${occurrence}` }
  })
}
